import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faUser,
  faPhone,
  faMapMarkerAlt,
  faCalendarDays,
  faClock,
  faMoneyBillWave,
  faCircleExclamation,
  faCircleCheck,
  faPrint,
  faHandsHelping,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "./hooks/useHerKart";

const STATUS_STYLES = {
  Requested: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  Confirmed: "border-cyan-500/30 bg-cyan-500/10 text-cyan-300",
  Completed: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  Cancelled: "border-red-500/30 bg-red-500/10 text-red-300",
};

const HerKartServiceBookingDetails = () => {
  const { bookingNo } = useParams();
  const navigate = useNavigate();
  const authState = useSelector((state) => state.authReducer);
  const { fetchServiceBookingByNo, updateServiceBooking } = useHerKart();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [status, setStatus] = useState("Requested");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await fetchServiceBookingByNo(bookingNo);
      if (response?.success) {
        setBooking(response.booking);
        setStatus(response.booking.status || "Requested");
        setRemarks(response.booking.remarks || "");
      } else {
        setMessage({ type: "error", text: "Booking not found." });
      }
    } catch (e) {
      console.error("Booking fetch error:", e);
      setMessage({ type: "error", text: "Could not load this booking." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingNo]);

  const isProvider = booking && booking.sellerCode === authState?.user?.think5whyscode;

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await updateServiceBooking(bookingNo, { status, remarks });
      if (response?.success) {
        setBooking(response.booking);
        setMessage({ type: "success", text: "Booking updated." });
      } else {
        setMessage({ type: "error", text: "Failed to update booking." });
      }
    } catch (e) {
      console.error("Booking update error:", e);
      setMessage({ type: "error", text: "Failed to update booking." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050816] text-white">
        <p className="text-slate-300">Loading booking...</p>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#050816] text-white">
        <p className="text-slate-300">{message?.text || "Booking not found."}</p>
        <Link to="/herkart/services" className="rounded-xl bg-pink-600 px-5 py-2 font-semibold hover:bg-pink-500">
          Back to Services
        </Link>
      </div>
    );
  }

  const bookedDate = new Date(booking.bookedAt).toLocaleString();
  const wantedDate = new Date(booking.preferredDate).toLocaleDateString();

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div>
              <h1 className="text-lg font-bold">Booking {booking.bookingNo}</h1>
              <p className="text-xs text-slate-400">
                {isProvider ? "Provider view" : "Your booking"}
              </p>
            </div>
          </div>

          <span
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${
              STATUS_STYLES[booking.status] || STATUS_STYLES.Requested
            }`}
          >
            {booking.status}
          </span>
        </div>
      </div>

      {message && (
        <div className="mx-auto max-w-4xl px-4 pt-4">
          <div
            className={`flex items-center gap-3 rounded-2xl border p-4 text-sm ${
              message.type === "success"
                ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-200"
                : "border-red-500/20 bg-red-500/10 text-red-200"
            }`}
          >
            <FontAwesomeIcon icon={message.type === "success" ? faCircleCheck : faCircleExclamation} />
            {message.text}
          </div>
        </div>
      )}

      <div className="mx-auto max-w-4xl px-4 py-6">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
          <h3 className="mb-5 flex items-center gap-2 text-xl font-bold text-cyan-100">
            <FontAwesomeIcon icon={faHandsHelping} className="text-pink-400" />
            {booking.serviceName}
          </h3>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faUser} className="mt-1 text-pink-400" />
              <div>
                <p className="text-xs text-slate-400">Customer</p>
                <p className="font-semibold text-gray-100">{booking.customer?.name}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faPhone} className="mt-1 text-pink-400" />
              <div>
                <p className="text-xs text-slate-400">Mobile</p>
                <p className="font-semibold text-gray-100">{booking.customer?.mobile}</p>
              </div>
            </div>

            <div className="md:col-span-2 flex items-start gap-3">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="mt-1 text-pink-400" />
              <div>
                <p className="text-xs text-slate-400">Service Address</p>
                <p className="font-semibold leading-7 text-gray-100">
                  {booking.customer?.house}, {booking.customer?.street}
                  {booking.customer?.landmark ? `, ${booking.customer.landmark}` : ""}
                  <br />
                  {booking.customer?.city}, {booking.customer?.state} - {booking.customer?.pincode}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faCalendarDays} className="mt-1 text-pink-400" />
              <div>
                <p className="text-xs text-slate-400">Preferred Date</p>
                <p className="font-semibold text-gray-100">{wantedDate}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FontAwesomeIcon icon={faClock} className="mt-1 text-pink-400" />
              <div>
                <p className="text-xs text-slate-400">Preferred Slot</p>
                <p className="font-semibold text-gray-100">
                  {booking.preferredSlot} {booking.duration ? `(${booking.duration})` : ""}
                </p>
              </div>
            </div>
          </div>

          {booking.customer?.notes && (
            <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-3 text-sm text-slate-300">
              <span className="font-semibold text-pink-300">Customer notes: </span>
              {booking.customer.notes}
            </div>
          )}
        </div>

        <div className="mt-6 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faMoneyBillWave} className="text-emerald-400" />
            <div>
              <h4 className="font-semibold text-cyan-50">Payment Mode</h4>
              <p className="text-sm text-slate-100">{booking.paymentMode}</p>
              <p className="mt-1 text-lg font-bold text-pink-400">₹{booking.price}</p>
            </div>
          </div>
        </div>

        {isProvider ? (
          <div className="mt-6 rounded-3xl border border-white/10 bg-white/5 p-6">
            <h3 className="mb-4 text-lg font-bold text-cyan-100">Update Booking</h3>

            <label className="mb-2 block text-sm text-slate-300">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-3 py-2 text-gray-100 outline-none focus:border-pink-500"
            >
              <option value="Requested">Requested</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>

            <label className="mb-2 mt-4 block text-sm text-slate-300">Remarks (visible to customer)</label>
            <input
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-3 py-2 text-gray-100 outline-none focus:border-pink-500"
              placeholder="e.g. Confirmed for 10 AM, please keep the area accessible"
            />

            <button
              onClick={handleSave}
              disabled={saving}
              className="mt-4 rounded-xl bg-pink-600 px-5 py-2 font-semibold hover:bg-pink-500 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save Update"}
            </button>
          </div>
        ) : (
          booking.remarks && (
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-300">
              <span className="font-semibold text-pink-300">Provider remarks: </span>
              {booking.remarks}
            </div>
          )
        )}

        <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="flex items-start gap-3">
            <FontAwesomeIcon icon={faCalendarDays} className="mt-1 text-pink-400" />
            <div>
              <h4 className="font-semibold text-gray-100">Booked On</h4>
              <p className="text-sm text-slate-300">{bookedDate}</p>
            </div>
          </div>
        </div>

        <button
          onClick={() => setShowInvoice((prev) => !prev)}
          className="mt-6 flex w-fit items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 font-semibold hover:bg-white/10"
        >
          <FontAwesomeIcon icon={faPrint} />
          {showInvoice ? "Hide Invoice" : "View / Print Invoice"}
        </button>

        {showInvoice && (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white p-6 text-black print:border-0">
            <div className="flex items-center justify-between border-b border-black/10 pb-3">
              <div>
                <h4 className="text-lg font-bold">Service Invoice</h4>
                <p className="text-xs text-slate-600">Booking No: {booking.bookingNo}</p>
              </div>
              <p className="text-xs text-slate-600">{bookedDate}</p>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-slate-500">Billed To</p>
                <p className="font-semibold">{booking.customer?.name}</p>
                <p>{booking.customer?.mobile}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Provider</p>
                <p className="font-semibold">{booking.entrepreneur || booking.store}</p>
                <p>{booking.storeCode}</p>
              </div>
            </div>

            <table className="mt-6 w-full text-sm">
              <thead>
                <tr className="border-b border-black/10 text-left text-slate-500">
                  <th className="py-2">Service</th>
                  <th className="py-2">Duration</th>
                  <th className="py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2">{booking.serviceName}</td>
                  <td className="py-2">{booking.duration || "—"}</td>
                  <td className="py-2 text-right">₹{booking.price}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="border-t border-black/10 font-bold">
                  <td className="py-2" colSpan={2}>
                    Total
                  </td>
                  <td className="py-2 text-right">₹{booking.price}</td>
                </tr>
              </tfoot>
            </table>

            <p className="mt-4 text-xs text-slate-500">
              GST not applicable — no tax registration on file for this phase.
            </p>

            <button
              onClick={() => window.print()}
              className="mt-4 rounded-xl bg-pink-600 px-4 py-2 font-semibold text-white hover:bg-pink-500 print:hidden"
            >
              <FontAwesomeIcon icon={faPrint} className="mr-2" />
              Print / Save as PDF
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HerKartServiceBookingDetails;
